import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ToolCallMessagePartProps } from '@assistant-ui/react';

// Internal or non-user-relevant keys to filter by default
const INTERNAL_KEYS = new Set([
  'pk',
  'sk',
  'gsi1pk',
  'gsi1sk',
  'gsi2pk',
  'gsi2sk',
  '__edb_e__',
  '__edb_v__',
]);

function parseResult(result: unknown): Record<string, unknown> | null {
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result as Record<string, unknown>;
  }
  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // ignore parse error
    }
  }
  return null;
}

export const EntityDetailTool = ({
  toolName,
  argsText,
  result,
  status,
}: ToolCallMessagePartProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (status.type === 'running') {
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        <CircularProgress size={16} />
        <Typography variant="body2" fontStyle="italic">
          {t('aiChat.tool.running', { action: t(`aiChat.tool.name.${toolName}`, toolName) })}
        </Typography>
      </Box>
    );
  }

  if (status.type === 'requires-action') {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 1 }}>
        <CardContent>
          <Typography variant="body2" mb={1}>
            {t(`aiChat.tool.name.${toolName}`, toolName)}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" size="small" autoFocus>
              {t('aiChat.tool.confirm')}
            </Button>
            <Button variant="text" size="small">
              {t('aiChat.tool.cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (status.type === 'incomplete') {
    return (
      <Box>
        <Chip
          icon={<ErrorOutlineIcon />}
          label={t('aiChat.tool.failed', { toolName: t(`aiChat.tool.name.${toolName}`, toolName) })}
          color="error"
          size="small"
          variant="outlined"
        />
        {argsText && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {argsText}
          </Typography>
        )}
      </Box>
    );
  }

  // Complete state: render detail card
  const entity = parseResult(result);

  if (!entity) {
    return (
      <Typography variant="body2" color="text.secondary">
        {/* eslint-disable-next-line @typescript-eslint/no-base-to-string */}
        {String(result ?? '')}
      </Typography>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const entityName = String(entity.name ?? entity.id ?? '');
  const allFields = Object.keys(entity).filter((k) => !INTERNAL_KEYS.has(k));
  const visibleFields = expanded ? allFields : allFields.slice(0, 8);
  const hasMore = allFields.length > 8;

  return (
    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {entityName && (
          <Typography variant="subtitle1" mb={1}>
            {entityName}
          </Typography>
        )}
        {visibleFields.map((key) => (
          <Box key={key} display="flex" gap={1} mb={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 120, flexShrink: 0 }}
            >
              {key}
            </Typography>
            <Typography variant="body2">
              {typeof entity[key] === 'object'
                ? JSON.stringify(entity[key])
                : // eslint-disable-next-line @typescript-eslint/no-base-to-string
                  String(entity[key] ?? '')}
            </Typography>
          </Box>
        ))}
        {hasMore && !expanded && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', display: 'block', mt: 0.5 }}
            onClick={() => {
              setExpanded(true);
            }}
          >
            {t('aiChat.tool.showMore')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
